'use client'

import React from 'react'
import styles from './styles.module.css'
import Marquee from 'react-fast-marquee'
import SmartLink from '../../SmartLink'
import SaleCountdown from '../../SaleCampaign/SaleCountdown'
import { getCampaignPhase, getDealGroups, PHASE_LIVE, PHASE_TEASER, msOf } from '../../../lib/saleCampaign'
import { pathWithRegion } from '../../../utils/regions'
import { EXPO_PROMO, isExpoPromoLive, expoPromoEndMs } from '../../../lib/expoPromo'

// Sim Racing Expo Frankfurt 2026 (lib/expoPromo): 15% off everything for every
// visitor, code applied automatically. Black bar with a countdown to the end.
// Ticks every second, so it lives in its own component.
// `region`: the page's URL region (null on content pages) so the link stays in the
// visitor's store, like every other header commerce link (region-currency e2e).
function ExpoPromoBar({ region = null }) {
  // null on the SSR + hydration render (both identical), then the real clock.
  const [now, setNow] = React.useState(null)
  React.useEffect(() => {
    setNow(Date.now())
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  if (now !== null && !isExpoPromoLive(now)) return null
  const items = [
    `${EXPO_PROMO.percent}% off everything`,
    'Applied automatically at checkout',
    'Already on sale? The bigger discount wins',
    '30-day money-back guarantee',
    '3-year warranty',
  ]
  return (
    <div className={styles.saleBar}>
      <SmartLink href={region ? pathWithRegion('/shop', region) : '/shop'} className={styles.saleBarLive}>
        <span className={styles.saleHeadline}>
          <span className={styles.saleDot} aria-hidden="true"/>
          Sim Racing Expo: {EXPO_PROMO.percent}% off everything
        </span>
        <span className={styles.saleMarquee}>
          <Marquee autoFill speed={40} gradient={false}>
            {items.map((t) => <span key={t} className={styles.saleMarqueeItem}>✓ {t}</span>)}
          </Marquee>
        </span>
        <SaleCountdown targetMs={expoPromoEndMs()} nowMs={now || 0} mounted={now !== null} size="sm" theme="dark" label="Ends in"/>
        <span className={styles.saleCta}>Shop now →</span>
      </SmartLink>
    </div>
  )
}

// Site-wide sale bar, driven by the `sale-campaign` dataset doc (lib/saleCampaign)
// instead of the hardcoded 2025 launch/end dates this used to carry.
//  - teaser (campaign.teaserStart -> start): "<bar.teaser>" + countdown to start
//  - live   (start -> end): "<bar.live>" + marquee + link
//  - otherwise: nothing.
//
// CLS + #111: the phase is decided on the SERVER (`salePhaseAtRender`, from
// SiteChrome) so the bar is part of the SSR HTML and reserves its height (it
// used to appear after hydration and push the whole page down ~78px on mobile).
// The SSR pass and the client's first render both use that phase with
// placeholder countdown digits, so they match; after mount the real clock takes
// over. `clockOffsetMs` is the preview-only shift (SALE_PREVIEW_NOW), 0 in
// production.
function AnnouncementBar({ saleCampaign: campaign, clockOffsetMs = 0, salePhaseAtRender = null, expoLiveAtRender = false, region = null }) {
  const [now, setNow] = React.useState(null)
  React.useEffect(() => {
    const read = () => Date.now() + (Number(clockOffsetMs) || 0)
    setNow(read())
    const id = setInterval(() => setNow(read()), 1000)
    return () => clearInterval(id)
  }, [clockOffsetMs])

  // Sim Expo: the server already knows the promo is live, so its bar is part of
  // the SSR HTML (reserves its height; no shift after hydration) and it wins
  // over the campaign bar while it runs.
  const mounted = now !== null
  if (mounted ? isExpoPromoLive(now) : expoLiveAtRender) return <ExpoPromoBar region={region}/>
  if (!campaign) return null
  const phase = mounted ? getCampaignPhase(campaign, now) : salePhaseAtRender
  if (phase !== PHASE_LIVE && phase !== PHASE_TEASER) return null

  const bar = campaign.bar || {}
  const href = `/${campaign.route}`

  if (phase === PHASE_TEASER) {
    return (
      <div className={styles.saleBar}>
        <SmartLink href={href} className={styles.saleBarLink}>
          <span className={styles.saleHeadline}>{bar.teaser || `${campaign.name} starts in`}</span>
          <SaleCountdown targetMs={msOf(campaign.start)} nowMs={now || 0} mounted={mounted} size="sm" theme="dark" showDays/>
        </SmartLink>
      </div>
    )
  }

  // Marquee lines are plain strings, or { text, group, when } tied to one deal
  // set: shown while that set is on ("live" / "upcoming", either if no
  // `when`). `regions` / `exceptRegions` limit a line to (or keep it out of)
  // some stores, e.g. no Lite Formula wheel line in the US; content pages with no
  // store region show the lines without `regions`. Set lines need the clock, so they join after mount; the SSR pass
  // and the first client render show the plain lines only.
  const groups = mounted ? getDealGroups(campaign, now, region) : []
  const showItem = (it) => {
    if (typeof it === 'string') return true
    if (!it || !it.text) return false
    if (Array.isArray(it.regions) && !it.regions.includes(region)) return false
    if (Array.isArray(it.exceptRegions) && region && it.exceptRegions.includes(region)) return false
    const g = groups.find((x) => x.key === it.group)
    if (!g) return false
    return !it.when || (it.when === 'live') === g.live
  }
  const listed = (Array.isArray(bar.marquee) ? bar.marquee : []).filter(showItem).map((it) => (typeof it === 'string' ? it : it.text))
  const items = listed.length ? listed : ['30-day money-back guarantee', '3-year warranty']

  return (
    <div className={styles.saleBar}>
      <SmartLink href={href} className={styles.saleBarLive}>
        <span className={styles.saleHeadline}>
          <span className={styles.saleDot} aria-hidden="true"/>
          {bar.live || `${campaign.name} is live`}
        </span>
        <span className={styles.saleMarquee}>
          <Marquee autoFill speed={40} gradient={false}>
            {items.map((t) => <span key={t} className={styles.saleMarqueeItem}>✓ {t}</span>)}
          </Marquee>
        </span>
        <span className={styles.saleCta}>{bar.cta || 'See deals'} →</span>
      </SmartLink>
    </div>
  )
}

export default AnnouncementBar
